import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { Bot, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import type {
  DataFetchParams,
  BulkToolbarContext,
  RowSelectionOptions,
  TableQueryState,
} from '@/components/DataTable';
import type { NetworkConnectionListItem } from '@kirin/api-client/contracts/shadow-ai';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  useOrgNetworkStats,
  useOrgNetworkDomains,
  useAgentNames,
  SHADOW_AI_REFRESH_INTERVAL,
} from '@/hooks/api/useShadowAiTools';
import {
  useClassifyDomain,
  useBulkClassify,
  useRemoveOrgIgnoreDomain,
  useRemoveClassifiedDomain,
} from '@/hooks/api/useNetworkDiscovery';
import { queryKeys } from '@/hooks/api/queryKeys';
import { Button } from '@/components/ui/button';
import { NetworkTrafficDashboard } from '../NetworkTrafficDashboard';
import { IdentifyAgentModal } from '../IdentifyAgentModal';
import {
  createNetworkDomainsColumns,
  createNetworkDomainsActionsColumn,
} from './columns';
import { ExpandedConnectionRow } from './ExpandedConnectionRow';
import { fetchNetworkDomains } from './api';
import { NETWORK_DOMAINS_TABLE_CONFIG } from './constants';
import {
  NetworkStatusFilterSelector,
  type NetworkStatusFilterValue,
} from './NetworkStatusFilterSelector';

export function DataTableNetworkDiscoveryContent({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const queryClient = useQueryClient();

  // KIR-3496: Status is a top-level scope (segmented control + summary
  // widgets), not a per-column filter. Default landing view = Unreviewed.
  const [status, setStatus] = useState<NetworkStatusFilterValue>('pending');
  const [identifyingConnection, setIdentifyingConnection] =
    useState<NetworkConnectionListItem | null>(null);
  // Set only for the bulk "Identify All" flow — the exact selected domains to
  // identify together (null for the per-row Identify action).
  const [bulkIdentifyDomains, setBulkIdentifyDomains] = useState<
    string[] | null
  >(null);

  // Open the per-row Identify modal (single connection, no bulk preset).
  const openRowIdentify = useCallback(
    (connection: NetworkConnectionListItem): void => {
      setBulkIdentifyDomains(null);
      setIdentifyingConnection(connection);
    },
    []
  );

  const closeIdentify = useCallback((): void => {
    setIdentifyingConnection(null);
    setBulkIdentifyDomains(null);
  }, []);

  const { data: statsData, isLoading: statsLoading } =
    useOrgNetworkStats(organizationId);

  const identifyModalOpen = identifyingConnection !== null;
  const { data: allDomains } = useOrgNetworkDomains(
    organizationId,
    identifyModalOpen
  );
  const { data: agentNames } = useAgentNames(organizationId, identifyModalOpen);

  const classifyMutation = useClassifyDomain(organizationId);
  const bulkClassifyMutation = useBulkClassify(organizationId);
  const removeIgnoreMutation = useRemoveOrgIgnoreDomain(organizationId);
  const removeClassifiedMutation = useRemoveClassifiedDomain(organizationId);

  const invalidateQueries = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.shadowAi.all });
  }, [queryClient]);

  const handleClassify = useCallback(
    async (
      domain: string,
      classification: 'ai_approved' | 'ai_flagged' | 'ignore',
      options?: { name?: string; category?: string }
    ): Promise<void> => {
      await classifyMutation.mutateAsync({ domain, classification, options });
      invalidateQueries();
    },
    [classifyMutation, invalidateQueries]
  );

  const handleIdentifyConfirm = useCallback(
    async (
      agentName: string,
      matchedDomains: string[],
      options?: { category?: string }
    ): Promise<void> => {
      if (matchedDomains.length === 0) return;
      if (matchedDomains.length === 1) {
        await handleClassify(matchedDomains[0], 'ai_approved', {
          name: agentName,
          category: options?.category,
        });
      } else {
        await bulkClassifyMutation.mutateAsync({
          domains: matchedDomains,
          classification: 'ai_approved',
          options: {
            name: agentName,
            category: options?.category,
          },
        });
        invalidateQueries();
      }
      closeIdentify();
    },
    [handleClassify, bulkClassifyMutation, invalidateQueries, closeIdentify]
  );

  // Stable per-row action callbacks (mutation.mutateAsync is referentially
  // stable in react-query), so the Actions column only recomputes on the
  // pending-state booleans below — never on unrelated re-renders.
  const revertIgnore = removeIgnoreMutation.mutateAsync;
  const revertClassified = removeClassifiedMutation.mutateAsync;
  const handleDismiss = useCallback(
    (domain: string) => void handleClassify(domain, 'ignore'),
    [handleClassify]
  );
  const handleRevertIgnore = useCallback(
    (domain: string) => void revertIgnore(domain).then(invalidateQueries),
    [revertIgnore, invalidateQueries]
  );
  const handleRevertClassified = useCallback(
    (domain: string) => void revertClassified(domain).then(invalidateQueries),
    [revertClassified, invalidateQueries]
  );

  const isClassifying = classifyMutation.isPending;
  const isReverting =
    removeIgnoreMutation.isPending || removeClassifiedMutation.isPending;

  // Data columns are kept stable (recomputed only when the scope changes) so
  // the per-column DYNAMIC filter closures (getQueryKey/queryFn) don't churn
  // every render — churn there re-subscribes useFilterOptions and destabilises
  // the filter dropdowns (KIR-3496).
  const dataColumns = useMemo(
    () =>
      createNetworkDomainsColumns(organizationId, {
        // Agent column is meaningful only in the Identified view.
        showAgentColumn: status === 'approved',
        endpointLabel: 'Endpoints',
        status,
      }),
    [organizationId, status]
  );

  const actionsColumn = useMemo(
    () =>
      createNetworkDomainsActionsColumn({
        onIdentify: openRowIdentify,
        onDismiss: handleDismiss,
        onRevertIgnore: handleRevertIgnore,
        onRevertClassified: handleRevertClassified,
        isClassifying,
        isReverting,
      }),
    [
      openRowIdentify,
      handleDismiss,
      handleRevertIgnore,
      handleRevertClassified,
      isClassifying,
      isReverting,
    ]
  );

  const columns = useMemo(
    () => [...dataColumns, actionsColumn],
    [dataColumns, actionsColumn]
  );

  const initialState = useMemo<Partial<TableQueryState>>(
    () => ({ sort: { field: 'hitCount', order: SortOrder.Desc } }),
    []
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchNetworkDomains(organizationId, params, signal, status),
    [organizationId, status]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      queryKeys.shadowAi.networkDomainsDataTable(organizationId, {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        status,
        filters: params.filters,
        search: params.search,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
      }),
    [organizationId, status]
  );

  // KIR-5098 (P1): this page had no main search; it matches the host or any
  // endpoint (name / ID) the host was reached from.
  const searchConfig = useMemo(
    () => ({
      placeholder: 'Search hosts or endpoints...',
      className: 'w-[260px]',
    }),
    []
  );

  const countScope = useMemo(
    () => ({ organizationId, status }),
    [organizationId, status]
  );

  // Bulk Identify / Dismiss only act on pending rows, so selection is offered
  // ONLY on the Unreviewed scope (all rows pending). Other scopes render no
  // checkbox column / bulk toolbar, so a user can't select rows the bulk
  // actions would silently ignore (KIR-3496 review).
  const rowSelection = useMemo<
    RowSelectionOptions<NetworkConnectionListItem> | undefined
  >(() => {
    if (status !== 'pending') return undefined;
    return {
      getRowId: row => row.domain,
      resetToken: status,
      renderBulkToolbar: (
        ctx: BulkToolbarContext<NetworkConnectionListItem>
      ) => {
        const pendingDomains = ctx.selectedRows
          .filter(r => r.status === 'pending')
          .map(r => r.domain);
        if (ctx.selectedRowIds.length === 0) return null;
        return (
          <div className="flex items-center gap-3 px-3 py-1 bg-muted border rounded-lg">
            <span className="text-sm font-medium">
              {ctx.selectedRowIds.length} selected
            </span>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={ctx.clearSelection}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              disabled={
                pendingDomains.length === 0 || bulkClassifyMutation.isPending
              }
              onClick={() => {
                const first = ctx.selectedRows.find(
                  r => r.status === 'pending'
                );
                if (first) {
                  setBulkIdentifyDomains(pendingDomains);
                  setIdentifyingConnection(first);
                }
              }}
            >
              <Bot className="h-4 w-4 mr-1" />
              Identify All
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              disabled={
                pendingDomains.length === 0 || bulkClassifyMutation.isPending
              }
              onClick={async () => {
                await bulkClassifyMutation.mutateAsync({
                  domains: pendingDomains,
                  classification: 'ignore',
                });
                invalidateQueries();
                ctx.clearSelection();
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Dismiss All
            </Button>
          </div>
        );
      },
    };
  }, [status, bulkClassifyMutation, invalidateQueries]);

  return (
    <div className="space-y-4">
      <NetworkTrafficDashboard
        stats={statsData}
        isLoading={statsLoading}
        onUnreviewedClick={() => setStatus('pending')}
        onIdentifiedClick={() => setStatus('approved')}
      />

      <NetworkStatusFilterSelector value={status} onChange={setStatus} />

      <DataTable<NetworkConnectionListItem>
        // Remount per status scope so the row set, the query, and the
        // (status-dependent) Agent column stay consistent — switching status
        // never leaves the previous scope's rows rendered against the new
        // scope's columns (KIR-3496).
        key={`network-${status}`}
        id={NETWORK_DOMAINS_TABLE_CONFIG.tableId}
        mode="server"
        columns={columns}
        initialState={initialState}
        search={searchConfig}
        errorBoundaryLabel="Something went wrong in the Network Discovery table"
        features={{
          enableSorting: true,
          enableRowExpansion: true,
          // Status is the top-level scope and drives a remount; keep the table's
          // filter/sort state internal so a remount reliably starts clean.
          enableUrlState: false,
          enableLocalPreferences: false,
        }}
        rowExpansion={{
          getRowId: row => row.domain,
          render: row => (
            <ExpandedConnectionRow
              organizationId={organizationId}
              row={row}
              showAgentColumn={status === 'approved'}
              // Same condition that gates `rowSelection` below.
              showSelectionColumn={status === 'pending'}
              statusScope={status}
            />
          ),
          contentClassName: 'bg-muted/30 p-4',
          rowClassName: 'bg-muted/10',
          keepMounted: false,
          showContainer: false,
        }}
        rowSelection={rowSelection}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        countScope={countScope}
        noDataLabel="No connections found."
        queryConfig={{
          staleTime: 30000,
          refetchOnWindowFocus: false,
          refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
        }}
      />

      <IdentifyAgentModal
        connection={identifyingConnection}
        allDomains={allDomains ?? []}
        existingAgentNames={agentNames ?? []}
        isOpen={identifyModalOpen}
        presetDomains={bulkIdentifyDomains ?? undefined}
        onClose={closeIdentify}
        onConfirm={handleIdentifyConfirm}
        isLoading={classifyMutation.isPending || bulkClassifyMutation.isPending}
      />
    </div>
  );
}

DataTableNetworkDiscoveryContent.displayName =
  'DataTableNetworkDiscoveryContent';
